// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useApolloClient, useMutation } from '@apollo/client';
import { RECURRING_PLANTING_STATUS } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { useGating, useUserContext } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import {
  DeleteForeverIcon,
  EditIcon,
  FileCopyIcon,
  PauseCircleOutlineIcon,
  PlayCircleOutlineIcon,
} from '../../lib/icons';
import { DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import * as routes from '../../routes';
import type {
  DeleteRecurringPlantingMutation,
  DeleteRecurringPlantingMutationVariables,
  UpdateRecurringPlantingStatusMutation,
  UpdateRecurringPlantingStatusMutationVariables,
} from '../../types/__generated__/graphql';
import { getTimeRanges } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { Confirmation } from '../common/dailog';
import { Options } from '../common/dropdown';
import AddRecurringPlantingDialog from './AddRecurringPlantingDialog';
import EditRecurringPlantingNameDialog from './EditRecurringPlantingNameDialog';
import EditRecurringPlantingNotesDialog from './EditRecurringPlantingNotesDialog';
import EditRecurringPlantingTimesDialog from './EditRecurringPlantingTimesDialog';
import EditRecurringPlantingTraysDialog from './EditRecurringPlantingTraysDialog';

interface Props {
  readonly recurringPlantingId: number;
  readonly useMoreIcon?: boolean;
  readonly status: string;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

const DELETE_MUTATION = gql`
  mutation DeleteRecurringPlanting($id: Int!) {
    deleteRecurringPlanting(input: { recurring_planting_id: $id }) {
      deleted_recurring_planting {
        id
      }
      deleted_plantings {
        id
      }
      deleted_tasks {
        id
      }
    }
  }
`;
const RECURRING_PLANTING_STATUS_MUT = gql`
  mutation UpdateRecurringPlantingStatus($id: Int!, $new_status: String!) {
    editRecurringPlantingStatus(input: { recurring_planting_id: $id, new_status: $new_status }) {
      updated_recurring_planting {
        id
        status
        updated_at
      }
      deleted_plantings {
        id
      }
      deleted_tasks {
        id
      }
      plantings_created {
        id
      }
      tasks_created {
        id
      }
    }
  }
`;

export default function RecurringPlantingOptions({ recurringPlantingId, status, useMoreIcon, onSuccess }: Props) {
  const { canUseBasic } = useGating();
  const navigate = useNavigate();
  const { timezone } = useUserContext();
  const [clonePlantingOpen, setClonePlantingOpen] = React.useState(false);
  const [editNameOpen, setEditNameOpen] = React.useState(false);
  const [editTraysOpen, setEditTraysOpen] = React.useState(false);
  const [editNotesOpen, setEditNotesOpen] = React.useState(false);
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [editTimesOpen, setEditTimesOpen] = React.useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
  const apolloClient = useApolloClient();
  const [deleteRecurringPlanting] = useMutation<
    DeleteRecurringPlantingMutation,
    DeleteRecurringPlantingMutationVariables
  >(DELETE_MUTATION);
  const [updateRecurringPlantingStatus] = useMutation<
    UpdateRecurringPlantingStatusMutation,
    UpdateRecurringPlantingStatusMutationVariables
  >(RECURRING_PLANTING_STATUS_MUT);

  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const deleteConfirmation = (
    <Confirmation
      open={deleteConfirmationOpen}
      onClose={() => {
        setDeleteConfirmationOpen(false);
        setOptionsClose(true);
      }}
      description="Are you sure you want to delete this recurring planting?"
      title="Delete Recurring Planting"
      onSubmit={() => {
        void deleteRecurringPlanting({
          variables: { id: recurringPlantingId },
          onCompleted: (completedData) => {
            enqueueSnackbar('Deleted recurring planting', { variant: 'success' });
            if (completedData.deleteRecurringPlanting != null) {
              evictCacheData({
                apolloClient,
                plantingIds: completedData.deleteRecurringPlanting.deleted_plantings.map((t) => t.id),
                taskIds: completedData.deleteRecurringPlanting.deleted_tasks.map((t) => t.id),
                recurringPlantingIds: [completedData.deleteRecurringPlanting.deleted_recurring_planting.id],
              });
            }
            setOptionsClose(true);
            setDeleteConfirmationOpen(false);
            navigate(routes.RECURRING_PLANTINGS);
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }}
    />
  );
  const editNameMenu = (
    <MenuItem
      key="edit-recurring-planting-name"
      onClick={() => {
        setEditNameOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Name
    </MenuItem>
  );
  const clone = (
    <MenuItem
      key="clone-recurring-planting"
      onClick={() => {
        setClonePlantingOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <FileCopyIcon />
      Clone
    </MenuItem>
  );
  const editTraysMenu = (
    <MenuItem
      key="edit-recurring-planting-trays"
      onClick={() => {
        setEditTraysOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Trays
    </MenuItem>
  );
  const editNotesMenu = (
    <MenuItem
      key="edit-recurring-planting-notes"
      onClick={() => {
        setEditNotesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Notes
    </MenuItem>
  );
  const pause = (
    <MenuItem
      key="pause-recurring-planting"
      onClick={() => {
        void updateRecurringPlantingStatus({
          variables: { id: recurringPlantingId, new_status: RECURRING_PLANTING_STATUS.PAUSED },
          onCompleted: (completedData) => {
            enqueueSnackbar('Paused recurring planting', { variant: 'success' });
            if (completedData.editRecurringPlantingStatus != null) {
              evictCacheData({
                apolloClient,
                plantingIds: completedData.editRecurringPlantingStatus.deleted_plantings.map((t) => t.id),
                taskIds: completedData.editRecurringPlantingStatus.deleted_tasks.map((t) => t.id),
              });
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
        setOptionsClose(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <PauseCircleOutlineIcon />
      Pause
    </MenuItem>
  );
  const unpause = (
    <MenuItem
      key="unpause-recurring-planting"
      onClick={() => {
        void updateRecurringPlantingStatus({
          refetchQueries: [
            {
              query: DASHBOARD_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
              },
            },
          ],
          update: (cache, { data }) => {
            const plantingsDeleted = (data?.editRecurringPlantingStatus?.deleted_plantings?.length ?? 0) > 0;
            const plantingsCreated = (data?.editRecurringPlantingStatus?.plantings_created?.length ?? 0) > 0;
            const tasksCreated = (data?.editRecurringPlantingStatus?.tasks_created?.length ?? 0) > 0;
            cache.modify({
              fields: {
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsDeleted || plantingsCreated ? DELETE : existingGroup;
                },
                plantings(existingPlantings, { DELETE }) {
                  return plantingsCreated || plantingsDeleted ? DELETE : existingPlantings;
                },
                harvests(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                task_groups(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                tasks(existingTasks, { DELETE }) {
                  return tasksCreated ? DELETE : existingTasks;
                },
                taskGroup(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                plantingGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantingTimeGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
              },
            });
          },
          variables: { id: recurringPlantingId, new_status: RECURRING_PLANTING_STATUS.ACTIVE },
          onCompleted: () => {
            enqueueSnackbar('Unpaused recurring planting', { variant: 'success' });
            onSuccess(true);
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
        setOptionsClose(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <PlayCircleOutlineIcon />
      Unpause
    </MenuItem>
  );
  const deleteItem = (
    <MenuItem
      key="delete-recurring-planting"
      onClick={() => {
        setDeleteConfirmationOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <DeleteForeverIcon />
      Delete
    </MenuItem>
  );
  const editTimesMenu = (
    <MenuItem
      key="edit-planting-times"
      onClick={() => {
        setEditTimesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Times
    </MenuItem>
  );

  const options = [
    editNameMenu,
    editTraysMenu,
    editTimesMenu,
    editNotesMenu,
    status === RECURRING_PLANTING_STATUS.ACTIVE ? pause : unpause,
    clone,
    deleteItem,
  ];

  return (
    <>
      <Options useMoreIcon={useMoreIcon} close={optionsClose} options={options} />
      {deleteConfirmationOpen && deleteConfirmation}
      {clonePlantingOpen && (
        <AddRecurringPlantingDialog
          cloneRecurringPlantingId={recurringPlantingId}
          isOpen={clonePlantingOpen}
          onClose={() => {
            setClonePlantingOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setClonePlantingOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {editTimesOpen && (
        <EditRecurringPlantingTimesDialog
          recurringPlantingId={recurringPlantingId}
          isOpen={editTimesOpen}
          onClose={() => {
            setEditTimesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditTimesOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {editTraysOpen && (
        <EditRecurringPlantingTraysDialog
          recurringPlantingId={recurringPlantingId}
          isOpen={editTraysOpen}
          onClose={() => {
            setEditTraysOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditTraysOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {editNotesOpen && (
        <EditRecurringPlantingNotesDialog
          recurringPlantingId={recurringPlantingId}
          isOpen={editNotesOpen}
          onClose={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditNotesOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {editNameOpen && (
        <EditRecurringPlantingNameDialog
          recurringPlantingId={recurringPlantingId}
          isOpen={editNameOpen}
          onClose={() => {
            setEditNameOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditNameOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
    </>
  );
}
